import { CUSTOM_EXPIRY_TIME } from "@/constants/campaignBuilder";
import type { NormalizedReminders, Reminder } from "@/interfaces/reminders";
import { isoToZonedSlot, zonedWallTimeToIso } from "@/utils/timezone-helper";

/**
 * Splits each stored instant into the date + time the pickers show. With `timeZone` that is the
 * campaign's zone (the reminder modal); without, the browser's — only the "is it still upcoming?"
 * split is read then (CampaignCard), and that doesn't depend on a zone.
 */
export function normalizeReminders(
  fields: (string | null | undefined)[],
  today = new Date(),
  timeZone?: string,
): NormalizedReminders {
  const sent: Reminder[] = [];
  const upcoming: Reminder[] = [];

  fields.forEach((raw, i) => {
    if (!raw) return;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return;

    const { date, time } = timeZone
      ? isoToZonedSlot(raw, timeZone)
      : {
          date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
          time: `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`,
        };
    const reminder: Reminder = { id: `reminder-${i + 1}`, date, time };

    if (dt < today) {
      sent.push(reminder);
    } else {
      upcoming.push(reminder);
    }
  });

  return { sent, upcoming };
}

export function toLocalDateStr(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** An ISO instant's own local calendar day, as `YYYY-MM-DD` — for calendar min/max bounds. */
export function isoToLocalDateStr(iso: string): string {
  return toLocalDateStr(new Date(iso));
}

export function getTomorrowStr(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return toLocalDateStr(tomorrow);
}

export function formatDisplayDate(dateStr: string) {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  const y = parseInt(parts[0] ?? "0", 10);
  const m = parseInt(parts[1] ?? "1", 10);
  const d = parseInt(parts[2] ?? "1", 10);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDisplayDateTime(dateStr: string, h: string, m: string, ap: "AM" | "PM") {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  const y = parseInt(parts[0] ?? "0", 10);
  const mo = parseInt(parts[1] ?? "1", 10);
  const d = parseInt(parts[2] ?? "1", 10);
  const dd = String(d).padStart(2, "0");
  const mm = String(mo).padStart(2, "0");
  const apLabel = ap === "AM" ? "a.m." : "p.m.";
  return `${mm}/${dd}/${y}, ${h}:${m} ${apLabel}`;
}

export function parseTime(time: string): { h: string; m: string; ap: "AM" | "PM" } {
  if (!time) return { h: "09", m: "00", ap: "AM" };
  const parts = time.split(":");
  const hh = parseInt(parts[0] ?? "9", 10);
  const mm = parseInt(parts[1] ?? "0", 10);
  return {
    h: String(hh % 12 || 12).padStart(2, "0"),
    m: String(mm).padStart(2, "0"),
    ap: hh >= 12 ? "PM" : "AM",
  };
}

export function buildTime(h: string, m: string, ap: "AM" | "PM") {
  let hh = parseInt(h, 10);
  if (ap === "PM" && hh !== 12) hh += 12;
  if (ap === "AM" && hh === 12) hh = 0;
  return `${String(hh).padStart(2, "0")}:${m}`;
}

export function hasPastDate(reminders: Reminder[], timeZone?: string): boolean {
  const now = Date.now();
  return reminders.some((r) => {
    if (!r.date || !r.time) return false;
    const iso = combineDateTime(r.date, r.time, timeZone);
    return iso !== null && new Date(iso).getTime() <= now;
  });
}

/**
 * Returns true if every reminder with a date+time is strictly after the
 * previous one in the array (chronological ascending order).
 */
export function hasChronologicalOrder(reminders: Reminder[], timeZone?: string): boolean {
  let previousMs: number | null = null;
  for (const r of reminders) {
    if (!r.date || !r.time) continue;
    const iso = combineDateTime(r.date, r.time, timeZone);
    if (iso === null) continue;
    const ms = new Date(iso).getTime();
    if (previousMs !== null && ms <= previousMs) return false;
    previousMs = ms;
  }
  return true;
}

// ── Send/expiry-aware validation — Campaign Builder Step 2 ───────────────────
//
// A client-side mirror of the backend's `validateReminderSchedule` and
// `describeReminderScheduleViolation` (apps/gfgf-api/src/utils/dates-helper.ts). Duplicated
// rather than shared — frontend and backend are separate runtimes here, same as
// `REMINDER_SCHEDULES` in constants/campaignBuilder.ts already duplicates the API's offset table
// label-for-label — so Step 2 can flag an invalid pick INLINE, at the input, the moment it's made,
// instead of only after a save round-trip lands as a toast on whatever screen the sender has since
// navigated to (Step 1/2/4 saves fire on the way out and never block the navigation). The server
// call stays authoritative; this is for immediate feedback only, and can drift by the few seconds
// between a check here and the save that follows it (e.g. an ASAP campaign's "now" reference).

export interface ReminderScheduleViolation {
  order: 1 | 2 | 3 | 4;
  reason: "not_after_send" | "not_before_expiry" | "not_after_previous_reminder";
}

export function validateReminderSchedule(
  dates: readonly [string | null, string | null, string | null, string | null],
  sendDate: string,
  expireDate: string | null,
): ReminderScheduleViolation | null {
  const sendMs = new Date(sendDate).getTime();
  const expireMs = expireDate ? new Date(expireDate).getTime() : null;
  let lastAcceptedMs = sendMs;

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    if (!date) continue;
    const candidateMs = new Date(date).getTime();
    const order = (i + 1) as 1 | 2 | 3 | 4;

    if (candidateMs <= lastAcceptedMs) {
      return {
        order,
        reason: lastAcceptedMs === sendMs ? "not_after_send" : "not_after_previous_reminder",
      };
    }
    if (expireMs !== null && candidateMs >= expireMs) {
      return { order, reason: "not_before_expiry" };
    }
    lastAcceptedMs = candidateMs;
  }
  return null;
}

export function describeReminderScheduleViolation(v: ReminderScheduleViolation): string {
  const reason =
    v.reason === "not_after_send"
      ? "must be after the campaign's send date"
      : v.reason === "not_before_expiry"
        ? "must be before the campaign's expiration date"
        : "must be after the previous reminder";
  return `Reminder ${v.order} ${reason}.`;
}

/**
 * `${date}T${time}` → ISO (defaults to 09:00). With `timeZone` it is read in that zone — what the
 * builder needs, mirroring the backend's own `combineDateTime`, which reads a builder payload in the
 * campaign's Schedule zone. Without one it is the browser's, which is what My Campaigns' reminder
 * modal needs: its save sends the browser offset alongside (api/reminders.ts).
 */
export function combineDateTime(
  date?: string | null,
  time?: string | null,
  timeZone?: string,
): string | null {
  if (!date) return null;
  const hhmm = (time || "09:00").slice(0, 5);
  if (timeZone) return zonedWallTimeToIso(date, hhmm, timeZone);
  const parsed = new Date(`${date}T${hhmm}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** `fromIso` (or "now" when null — an ASAP campaign's own fallback) shifted by `days`. */
export function addDaysIso(fromIso: string | null, days: number): string {
  const base = fromIso ? new Date(fromIso) : new Date();
  return new Date(base.getTime() + days * 86_400_000).toISOString();
}

/** `fromIso` (or "now" when null) shifted by whole calendar `months` — the "6 months" expiry
 *  preset counts in months, not 180 days (mirrors the server's addMonths). */
export function addMonthsIso(fromIso: string | null, months: number): string {
  const base = fromIso ? new Date(fromIso) : new Date();
  base.setMonth(base.getMonth() + months);
  return base.toISOString();
}

/**
 * The calendar bounds for ONE reminder row — the same window `validateReminderSchedule` checks,
 * but disabling the impossible days outright rather than only flagging a pick after the fact.
 *
 * Day-granularity, like the picker's own `isPast` cutoff. A row's floor is the day AFTER its
 * predecessor (the send instant, or the previous reminder), so any time chosen that day is
 * guaranteed valid. Its ceiling is whichever comes first — the campaign's expiry, or the NEXT
 * reminder's currently-shown date — and is EXCLUSIVE for the same reason: an expiry at 01:11
 * would otherwise admit 15:00 on its own day.
 *
 * Bounding both directions means an invalid order can't be built in the first place: to move
 * reminder 2 past reminder 3, the sender has to move reminder 3 first, the way a schedule won't
 * let you drag a task past a dependency without moving the dependency itself.
 *
 * Shared by Campaign Builder Step 2 and My Campaigns' Notifications modal. `dates` is each row's
 * current instant in row order (null = not set); a slot the surface isn't showing must be null,
 * so a row that isn't on screen can't bound one that is.
 */
export function reminderDateBounds(
  dates: readonly (string | null)[],
  idx: number,
  sendDate: string,
  expireDate: string | null,
  timeZone?: string,
): { minDate: string; maxDate: string | undefined } {
  // Calendar days in the zone the picker's dates are read in (the builder's), else the browser's.
  const dayOf = (iso: string) =>
    timeZone ? isoToZonedSlot(iso, timeZone).date : isoToLocalDateStr(iso);
  const priorIso = idx > 0 ? (dates[idx - 1] ?? null) : null;
  const minDate = dayOf(addDaysIso(priorIso ?? sendDate, 1));

  const expiryMaxDate = expireDate ? dayOf(expireDate) : undefined;
  const nextIso = dates[idx + 1] ?? null;
  const nextMaxDate = nextIso ? dayOf(nextIso) : undefined;
  if (expiryMaxDate !== undefined && nextMaxDate !== undefined) {
    return { minDate, maxDate: expiryMaxDate < nextMaxDate ? expiryMaxDate : nextMaxDate };
  }
  return { minDate, maxDate: nextMaxDate ?? expiryMaxDate };
}

/**
 * The same send/expiry pair `CampaignBuilderService.updateDraft` resolves server-side, computed
 * here from the same Step 2 context fields (`Schedule`/`Expiration`'s own state) so a live pick
 * can be checked before it's ever sent. `sendDate` is "now" for ASAP (no scheduledDate exists
 * pre-launch); `expireDate` is null when nothing has been chosen yet.
 */
export function resolveEffectiveWindow(schedule: {
  scheduleMode: "asap" | "certain" | "different";
  scheduleDate: string;
  scheduleTime: string;
  expiryMode: "30" | "60" | "90" | "6mo" | "custom" | undefined;
  customExpiryDate: string;
  /** IANA zone the Schedule step's wall-clock values are read in, as the server reads them. */
  timeZone: string;
}): { sendDate: string; expireDate: string | null } {
  const scheduledDate =
    schedule.scheduleMode === "certain"
      ? combineDateTime(schedule.scheduleDate, schedule.scheduleTime, schedule.timeZone)
      : null;
  const sendDate = scheduledDate ?? new Date().toISOString();

  const expireDate =
    schedule.expiryMode === "custom"
      ? combineDateTime(schedule.customExpiryDate, CUSTOM_EXPIRY_TIME, schedule.timeZone)
      : schedule.expiryMode === "6mo"
        ? addMonthsIso(scheduledDate, 6)
        : schedule.expiryMode
          ? addDaysIso(scheduledDate, Number(schedule.expiryMode))
          : null;

  return { sendDate, expireDate };
}

// ── Auto-calculated reminder preview — Campaign Builder Step 2 ───────────────
//
// A client-side mirror of the backend's `computeReminderDates`
// (apps/gfgf-api/src/utils/dates-helper.ts), duplicated for the same reason its neighbours
// `validateReminderSchedule` and `REMINDER_SCHEDULES` already are: Step 2 has to answer "what
// dates will this save?" AT the input, while the sender is still choosing. It cannot ask the
// server — a brand-new campaign has no id yet, so `useReminders` is disabled outright, and even
// on an existing draft the schedule/expiry only reach the API when the sender LEAVES the step.
// Without this the sender sets an expiry and watches four empty pickers, with no way to tell a
// window too short to hold reminders from a screen that simply has not loaded.
//
// The server stays authoritative: it recomputes on save and whatever it returns replaces this.
// Keep the offsets below in step with REMINDER_OFFSETS in dates-helper.ts.

const PREVIEW_OFFSETS: Record<
  number,
  ReadonlyArray<{ from: "send" | "expiry"; hours?: number; days?: number }>
> = {
  4: [
    { from: "send", hours: 48 },
    { from: "send", days: 7 },
    { from: "expiry", days: -7 },
    { from: "expiry", days: -1 },
  ],
  3: [
    { from: "send", hours: 48 },
    { from: "send", days: 7 },
    { from: "expiry", days: -2 },
  ],
  2: [
    { from: "send", hours: 48 },
    { from: "expiry", days: -7 },
  ],
  1: [{ from: "send", days: 7 }],
  0: [],
};

/** Calendar-day arithmetic, like the API's `addDays` — NOT `addDaysIso` above, which adds a fixed
 *  86_400_000 ms and so lands an hour off across a DST boundary the server would have absorbed. */
function addPreviewDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function addPreviewHours(iso: string, hours: number): string {
  return new Date(new Date(iso).getTime() + hours * 3_600_000).toISOString();
}

/** One schedule laid out in full, or null when it does not fit — mirrors `layOutReminderSchedule`. */
function layOutPreview(
  sendDate: string,
  expireDate: string | null,
  count: number,
  timeZone: string,
): (string | null)[] | null {
  const offsets = PREVIEW_OFFSETS[count];
  if (!offsets) return null;

  const sendMs = new Date(sendDate).getTime();
  const expireMs = expireDate ? new Date(expireDate).getTime() : null;
  if (Number.isNaN(sendMs)) return null;

  const result: (string | null)[] = [null, null, null, null];
  let lastMs = sendMs;
  let lastDay: string | null = null;

  for (const [i, offset] of offsets.entries()) {
    if (offset.from === "expiry" && expireDate === null) return null;
    const candidate =
      offset.from === "send"
        ? offset.hours !== undefined
          ? addPreviewHours(sendDate, offset.hours)
          : addPreviewDays(sendDate, offset.days ?? 0)
        : addPreviewDays(expireDate as string, offset.days ?? 0);
    const candidateMs = new Date(candidate).getTime();
    if (Number.isNaN(candidateMs)) return null;

    if (!(candidateMs > lastMs && (expireMs === null || candidateMs < expireMs))) return null;
    const candidateDay = isoToZonedSlot(candidate, timeZone).date;
    if (candidateDay === lastDay) return null;

    result[i] = candidate;
    lastMs = candidateMs;
    lastDay = candidateDay;
  }

  return result;
}

/**
 * The four dates a save would produce right now, as ISO instants (null = that slot gets none).
 *
 * Degrades the WHOLE schedule rather than dropping slots: a window that cannot hold four
 * reminders falls back to the three-reminder schedule, which has its own offsets, so the result
 * is always contiguous — never a gap between two set slots. An all-null answer is a real answer:
 * the window is too short for any schedule.
 */
export function previewReminderDates(
  sendDate: string,
  expireDate: string | null,
  reminderCount: number,
  timeZone: string,
): (string | null)[] {
  for (let count = reminderCount; count >= 1; count -= 1) {
    const attempt = layOutPreview(sendDate, expireDate, count, timeZone);
    if (attempt) return attempt;
  }
  return [null, null, null, null];
}
